import React, { memo } from 'react';

import type { Token } from '@/lib/highlight-client';

interface TokenRendererProps {
  tokens: Token[];
}

/**
 * Safely renders Shiki tokens as React elements.
 * This completely avoids dangerouslySetInnerHTML and XSS vulnerabilities.
 */
export const TokenRenderer = memo(function TokenRenderer({ tokens }: TokenRendererProps) {
  if (!tokens || tokens.length === 0) {
    return <span>{'\n'}</span>;
  }

  return (
    <>
      {tokens.map((token, index) => {
        // If token has no styling, just return the content
        if (!token.color && !token.fontStyle) {
          return <span key={index}>{token.content}</span>;
        }

        // Build style object from token properties
        const style: React.CSSProperties = {};

        if (token.color) {
          style.color = token.color;
        }

        if (token.fontStyle) {
          // Shiki uses bitwise flags for font styles
          if (token.fontStyle & 1) style.fontStyle = 'italic';
          if (token.fontStyle & 2) style.fontWeight = 'bold';
          if (token.fontStyle & 4) style.textDecoration = 'underline';
        }

        return (
          <span key={index} style={style}>
            {token.content}
          </span>
        );
      })}
    </>
  );
});

interface LineTokenRendererProps {
  tokens: Token[] | null;
  fallback: string;
}

/**
 * Renders a line of tokens with fallback to plain text.
 */
export const LineTokenRenderer = memo(function LineTokenRenderer({ tokens, fallback }: LineTokenRendererProps) {
  if (!tokens) {
    return <span>{fallback}</span>;
  }

  return <TokenRenderer tokens={tokens} />;
});
